import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Layout, Menu, Typography, Card, Table, Button, Tag, Avatar, Drawer, List, Empty, Spin, Space, Image, Alert, Modal, Input, Form, Select, Tabs, Popconfirm, Tooltip, Result, message, Row, Col, DatePicker, Statistic } from 'antd';
import { UserOutlined, TeamOutlined, LogoutOutlined, MenuUnfoldOutlined, MenuFoldOutlined, TrophyOutlined, ReloadOutlined, RollbackOutlined, LinkOutlined, SearchOutlined, DashboardOutlined, CalendarOutlined, PlusOutlined, ReadOutlined } from '@ant-design/icons';
import { useAuth } from '../services/useAuth';
import { teacherAPI, studentAPI, scheduleAPI } from '../services/api';
import type { StudentResponse, ErrorResponse } from '../services/api';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../styles/common.css';
import { Calendar, momentLocalizer, View, ToolbarProps as RBCToolbarProps } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios, { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';

const { Header, Sider, Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

// 设置日历本地化
const localizer = momentLocalizer(moment);

// Define Breakpoint type locally
type Breakpoint = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

// 定义学生成就接口类型
interface StudentAchievement {
  id: number;
  studentId: number;
  achievementDate: string;
  description: string;
  title?: string;
  imageUrl?: string;
  errorMessage?: string;
}

// 定义关联学生的接口
interface ConnectStudentFormValues {
  studentId: number;
  searchKeyword?: string;
}

// 定义课程事件接口
interface CourseEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  studentId: number;
  studentName: string;
  courseType?: string;  // 添加课程类型
  checkedIn?: boolean;  // 添加签到状态
  checkInTime?: Date;   // 添加签到时间
}

// 定义教师培训接口
interface TeacherTraining {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: 'UPCOMING' | 'ONGOING' | 'COMPLETED' | 'CANCELED';
  location?: string;
  trainer?: string;
  trainingType?: string;
  materials?: string[];
}

const TeacherDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState<StudentResponse[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentResponse | null>(null);
  const [achievements, setAchievements] = useState<StudentAchievement[]>([]);
  
  // 添加菜单项状态
  const [activeMenuKey, setActiveMenuKey] = useState<string>('1');
  // 课程安排数据（后期可从API获取）
  const [courseEvents, setCourseEvents] = useState<CourseEvent[]>([]);
  
  // 添加排课相关状态
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [selectedStudentForSchedule, setSelectedStudentForSchedule] = useState<StudentResponse | null>(null);
  const [timeSlots, setTimeSlots] = useState<Array<{startTime: Date, endTime: Date}>>([]);
  const [scheduleFormVisible, setScheduleFormVisible] = useState(false);
  
  // 添加课程详情模态框状态
  const [courseDetailModalVisible, setCourseDetailModalVisible] = useState(false);
  const [selectedCourseEvent, setSelectedCourseEvent] = useState<CourseEvent | null>(null);
  const [editingCourse, setEditingCourse] = useState(false);
  const [editedCourseStart, setEditedCourseStart] = useState<Date | null>(null);
  const [editedCourseEnd, setEditedCourseEnd] = useState<Date | null>(null);
  
  // Loading状态
  const [loading, setLoading] = useState({
    students: false,
    achievements: false,
    courses: false
  });
  
  // 添加错误状态
  const [error, setError] = useState({
    students: false,
    achievements: null as string | null,
    message: ''
  });
  
  // 记录加载失败次数，防止无限循环
  const failCountRef = useRef(0);
  
  // 响应式布局相关状态
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 记录上次请求时间
  const lastRequestTimeRef = useRef(0);
  // 记录强制刷新标志
  const forceRefreshRef = useRef(false);

  // 添加教师培训相关状态
  const [trainings, setTrainings] = useState<TeacherTraining[]>([]);
  const [loadingTrainings, setLoadingTrainings] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<TeacherTraining | null>(null);
  const [trainingDetailModalVisible, setTrainingDetailModalVisible] = useState(false);

  // 添加签到统计状态
  const [checkInStats, setCheckInStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState<[moment.Moment, moment.Moment]>(
    [moment().subtract(1, 'month'), moment()]
  );

  // 自定义工具栏组件
  const CustomToolbar: React.FC<RBCToolbarProps<CourseEvent, object>> = (props) => {
    const goToBack = () => {
      props.onNavigate('PREV');
    };
    
    const goToNext = () => {
      props.onNavigate('NEXT');
    };
    
    const goToCurrent = () => {
      props.onNavigate('TODAY');
    };
    
    const label = () => {
      return (
        <span>{props.label}</span>
      );
    };
    
    // 根据当前视图确定显示的标签文本
    const prevLabel = props.view === 'week' ? '上一周' : '上一月';
    const nextLabel = props.view === 'week' ? '下一周' : '下一月';
    
    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={goToCurrent}>今天</button>
          <button type="button" onClick={goToBack}>{prevLabel}</button>
          <button type="button" onClick={goToNext}>{nextLabel}</button>
        </span>
        <span className="rbc-toolbar-label">{label()}</span>
        <span className="rbc-btn-group">
          <button 
            type="button" 
            className={props.view === 'month' ? 'rbc-active' : ''} 
            onClick={() => props.onView('month')}
          >
            月视图
          </button>
          <button 
            type="button" 
            className={props.view === 'week' ? 'rbc-active' : ''} 
            onClick={() => props.onView('week')}
          >
            周视图
          </button>
        </span>
      </div>
    );
  };

  // 加载学生列表
  const loadStudents = useCallback(async () => {
    if (!user?.userId) {
      setError({
        students: true,
        achievements: null,
        message: '用户信息不完整，无法获取学员列表'
      });
      return;
    }
    
    // 限制连续请求的频率
    const now = Date.now();
    if (now - lastRequestTimeRef.current < 2000) { // 2秒内不允许重复请求
      console.log('请求过于频繁，稍后再试');
      message.info('请求过于频繁，请稍后再试');
      return;
    }
    lastRequestTimeRef.current = now;
    
    // 如果已经连续失败多次，不再自动尝试，但允许用户手动触发
    if (failCountRef.current > 3 && !forceRefreshRef.current) {
      setError({
        students: true,
        achievements: null,
        message: '多次获取学员列表失败，请检查网络连接或联系管理员，点击刷新按钮重试'
      });
      return;
    }
    
    setLoading(prev => ({ ...prev, students: true }));
    setError(prev => ({ ...prev, students: false, message: '' }));
    forceRefreshRef.current = false; // 重置强制刷新标志
    
    try {
      console.log(`开始获取教师(ID:${user.userId})的学员列表...`);
      
      // 显示加载中提示
      const loadingMsg = message.loading('正在获取学员列表...', 0);
      
      // 确保用户ID是数字类型
      const teacherId = parseInt(String(user.userId), 10);
      if (isNaN(teacherId) || teacherId <= 0) {
        console.error(`无效的教师ID: ${user.userId}`);
        setError({
          students: true,
          achievements: null,
          message: `无效的教师ID: ${user.userId}`
        });
        loadingMsg();
        return;
      }
      
      // 使用教师-学生关系API获取学生列表
      const response = await teacherAPI.getTeacherStudents(teacherId);
      
      // 关闭加载中提示
      loadingMsg();
      
      console.log('API响应:', response);
      
      if (Array.isArray(response)) {
        // 正常返回了学生数组
        console.log(`成功获取教师学员，共${response.length}条记录`);
        
        // 验证学生数据的有效性
        const validStudents = response.filter(student => 
          student && typeof student === 'object' && student.id && student.name
        );
        
        if (validStudents.length !== response.length) {
          console.warn(`过滤掉了${response.length - validStudents.length}条无效的学生记录`);
        }
        
        setStudents(validStudents);
        
        // 成功获取后显示提示
        if (validStudents.length > 0) {
          message.success(`成功获取${validStudents.length}名学员信息`);
          
          // 调试打印第一个学生信息
          console.log('学生信息示例:', validStudents[0]);
        } else {
          message.info('当前没有关联的学员');
        }
        
        // 重置失败计数
        failCountRef.current = 0;
      } else if (response && typeof response === 'object' && 'message' in response) {
        // 服务器返回了错误对象
        console.warn('API返回了错误对象而不是学生数组:', response);
        setStudents([]);
        
        const errorMsg = (response as ErrorResponse).message || '服务器返回错误';
        setError({
          students: true,
          achievements: null,
          message: errorMsg
        });
        
        message.error(`获取学员列表失败: ${errorMsg}`);
        failCountRef.current += 1;
      } else {
        // 其他意外情况
        console.warn('API返回的学生数据格式不正确:', response);
        setStudents([]);
        setError({
          students: true,
          achievements: null,
          message: '服务器返回的数据格式不正确'
        });
        message.error('获取学员列表失败: 数据格式不正确');
        failCountRef.current += 1;
      }
    } catch (error: any) {
      console.error('加载学生列表失败:', error);
      setStudents([]);
      
      // 提取更详细的错误信息
      let errorMessage = '获取学员列表失败';
      
      if (error.message) {
        errorMessage = error.message;
        
        // 特殊处理网络错误
        if (error.message.includes('Network Error')) {
          errorMessage = '网络连接错误，无法连接到服务器，请检查您的网络连接';
        } else if (error.message.includes('timeout')) {
          errorMessage = '请求超时，服务器响应时间过长，请稍后再试';
        }
      }
      
      // 显示错误信息
      setError({
        students: true,
        achievements: null,
        message: errorMessage
      });
      message.error(errorMessage);
      
      // 增加失败计数
      failCountRef.current += 1;
    } finally {
      setLoading(prev => ({ ...prev, students: false }));
    }
  }, [user?.userId]);

  // 强制刷新学生列表
  const forceRefreshStudents = useCallback(() => {
    console.log('强制刷新学生列表');
    forceRefreshRef.current = true; // 设置强制刷新标志
    loadStudents();
  }, [loadStudents]);
  
  // 加载学生成就
  const loadAchievements = useCallback(async (studentId: number) => {
    setLoading(prev => ({ ...prev, achievements: true }));
    setAchievements([]); // 清空之前的数据
    setError(prev => ({ ...prev, achievements: null })); // 清除之前的错误
    
    try {
      const hide = message.loading('加载学习成果中...', 0);
      
      const data = await studentAPI.getAchievements(studentId);
      
      // 关闭加载消息
      hide();
      
      if (data && Array.isArray(data)) {
        // 设置学习成果数据
        setAchievements(data);
        
        // 添加成功提示
        if (data.length > 0) {
          message.success(`成功加载${data.length}条学习成果`);
        } else {
          message.info('该学生暂无学习成果记录');
        }
      } else {
        message.warning('获取到的学习成果数据格式不正确');
        setAchievements([]);
      }
    } catch (error: unknown) {
      console.error('加载学习成果失败:', error);
      setAchievements([]);
      
      let errorMessage = '加载学习成果失败，请稍后重试';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // 特殊处理常见错误
        if (error.message.includes('权限')) {
          errorMessage = '您没有权限查看此学生的学习成果，请检查与该学生的关联关系';
          message.error('权限错误：' + errorMessage, 5);
        } else if (error.message.includes('超时')) {
          errorMessage = '服务器响应超时，请稍后再试';
          message.error('连接超时：' + errorMessage, 3);
        } else if (error.message.includes('网络')) {
          errorMessage = '网络连接错误，请检查您的网络连接';
          message.error('网络错误：' + errorMessage, 3);
        } else {
          message.error(errorMessage, 3);
        }
      } else {
        message.error(errorMessage, 3);
      }
      
      // 更新错误状态
      setError(prev => ({ ...prev, achievements: errorMessage }));
    } finally {
      setLoading(prev => ({ ...prev, achievements: false }));
    }
  }, []);

  // 响应窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    // 初始检查
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 初始化 - 加载学生列表，但修改依赖项以避免循环
  useEffect(() => {
    if (user?.userId) {
      // 确保只在组件挂载时或用户ID变化时加载一次
      loadStudents();
    }
  }, [user?.userId]); // 移除loadStudents依赖

  // 切换侧边栏显示/隐藏
  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerVisible(!drawerVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // 处理菜单点击，在移动端自动关闭抽屉
  const handleMenuClick = (key: string) => {
    setActiveMenuKey(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
    
    // 如果从学习成果页面切换到其他页面，重置选中的学生
    if (selectedStudent && key !== selectedStudent.id.toString()) {
      setSelectedStudent(null);
    }
    
    // 如果切换到课程安排菜单，加载课程
    if (key === '2' && user?.userId) {
      loadTeacherCourses();
    }
  };

  // 处理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 显示学生成就
  const showStudentAchievements = (student: StudentResponse) => {
    if (!student || !student.id) {
      message.error('无效的学生信息');
      return;
    }
    
    // 详细记录当前查看的学生信息
    console.log(`查看学生学习成果:`, {
      id: student.id,
      name: student.name,
      grade: student.grade || '未知',
      gender: student.gender || '未知',
      age: student.age || '未知',
    });
    
    // 先设置选中的学生
    setSelectedStudent(student);
    
    // 清除之前的错误
    setError(prev => ({ ...prev, achievements: null }));
    
    // 显示加载中提示
    const hide = message.loading({
      content: `正在加载${student.name}的学习成果...`,
      duration: 0,
    });
    
    // 加载学习成果
    loadAchievements(student.id)
      .then(() => {
        // 成功加载后处理
        console.log(`成功加载学生(ID:${student.id})的学习成果`);
      })
      .catch(error => {
        // 出错处理
        console.error('加载学习成果失败:', error);
        
        // 设置错误状态 - 注意这部分由loadAchievements已经处理
        let errorMessage = '网络错误，请稍后重试';
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        setError(prev => ({ ...prev, achievements: errorMessage }));
      })
      .finally(() => {
        // 无论成功失败都关闭加载提示
        hide();
      });
  };
  
  // 渲染侧边栏菜单
  const sideMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[activeMenuKey]}
      style={{ height: '100%', borderRight: 0 }}
      onClick={({key}) => handleMenuClick(key)}
    >
      <Menu.Item key="1" icon={<TeamOutlined />}>
        学员管理
      </Menu.Item>
      <Menu.Item key="2" icon={<CalendarOutlined />}>
        课程安排
      </Menu.Item>
      <Menu.Item key="3" icon={<DashboardOutlined />}>
        签到统计
      </Menu.Item>
      <Menu.Item key="4" icon={<ReadOutlined />}>
        课程直播
      </Menu.Item>
    </Menu>
  );

  // 在renderStudentsList方法中添加显示重试按钮
  const renderStudentsList = () => {
    const { students: hasError, message: errorMessage } = error;
    
    if (loading.students) {
      return <Spin tip="加载学员列表中..." />;
    }
    
    if (hasError) {
      return (
        <div className="error-container">
          <Alert 
            message="获取学员列表失败" 
            description={errorMessage}
            type="error"
            showIcon
            action={
              <Button 
                type="primary" 
                onClick={forceRefreshStudents} 
                icon={<ReloadOutlined />}
                loading={loading.students}
              >
                重新加载
              </Button>
            }
          />
        </div>
      );
    }
    
    if (students.length === 0) {
      return (
        <Empty
          description="暂无关联学员"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Space>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={forceRefreshStudents}
              loading={loading.students}
            >
              刷新
            </Button>
          </Space>
        </Empty>
      );
    }
    
    // 定义表格列
    const columns = [
      {
        title: '序号',
        dataIndex: 'index',
        key: 'index',
        width: 80,
        align: 'center' as const,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
        width: 120,
        align: 'center' as const,
        render: (text: string, record: StudentResponse) => text,
      },
      {
        title: '年龄',
        dataIndex: 'age',
        key: 'age',
        width: 80,
        align: 'center' as const,
        render: (age: number | undefined, record: StudentResponse) => {
          const ageDisplay = age || '--';
          
          // 如果有出生日期，显示提示信息
          if (record.birthDate) {
            return (
              <Tooltip title={`根据出生日期 ${moment(record.birthDate).format('YYYY-MM-DD')} 计算`}>
                {ageDisplay}
              </Tooltip>
            );
          }
          
          return ageDisplay;
        },
      },
      {
        title: '性别',
        dataIndex: 'gender',
        key: 'gender',
        width: 80,
        align: 'center' as const,
        render: (gender: string | undefined) => gender || '',
      },
      {
        title: '在读年级',
        dataIndex: 'grade',
        key: 'grade',
        width: 120,
        align: 'center' as const,
        render: (grade: string | undefined) => {
          if (!grade) return '未知';
          
          // 高亮显示年级，不同阶段使用不同颜色
          let color = 'default';
          if (grade.includes('幼儿园')) {
            color = 'purple';
          } else if (grade.includes('小学')) {
            color = 'blue';
          } else if (grade.includes('初中')) {
            color = 'green';
          } else if (grade.includes('高中')) {
            color = 'orange';
          }
          
          return <Tag color={color}>{grade}</Tag>;
        },
      },
      {
        title: '授课类型',
        dataIndex: 'courseType',
        key: 'courseType',
        width: 120,
        align: 'center' as const,
        render: (courseType: string | undefined) => {
          if (!courseType) return '未设置';
          return courseType;
        },
      },
      {
        title: '授课方式',
        dataIndex: 'courseMode',
        key: 'courseMode',
        width: 120,
        align: 'center' as const,
        render: (courseMode: string | undefined) => {
          if (!courseMode) return '未设置';
          return courseMode;
        },
      },
      {
        title: '操作',
        key: 'action',
        width: 120,
        align: 'center' as const,
        render: (_: any, record: StudentResponse) => (
          <Button
            type="primary"
            onClick={() => showStudentAchievements(record)}
            size="small"
          >
            查看学习成果
          </Button>
        ),
      },
    ];
    
    return (
      <div className="students-list-container">
        <div className="list-header">
          <Title level={4}>我的学员列表</Title>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={forceRefreshStudents}
            loading={loading.students}
          >
            刷新
          </Button>
        </div>
        
        {/* 添加表格样式 */}
        <style>
          {`
            .students-table .ant-table-thead > tr > th {
              background-color: #f5f5f5;
              text-align: center;
              font-weight: 500;
              padding: 12px 8px;
            }
            
            .students-table .ant-table-tbody > tr > td {
              padding: 12px 8px;
              text-align: center;
            }
            
            .students-table .ant-tag {
              margin-right: 0;
            }
            
            .students-table .ant-table-column-title {
              display: inline-block;
              width: 100%;
            }
          `}
        </style>
        
        <Table
          dataSource={students}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          bordered
          size="middle"
          className="students-table"
          scroll={{ x: 'max-content' }}
        />
      </div>
    );
  };
  
  // 渲染学生成就列表
  const renderAchievementsList = () => {
    if (!selectedStudent) {
      return null;
    }
    
    if (loading.achievements) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Spin tip="加载学习成果中..." size="large" />
        </div>
      );
    }
    
    // 错误处理
    if (error.achievements) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Result
            status="warning"
            title="获取学习成果失败"
            subTitle={error.achievements || "未知错误，请稍后再试"}
            extra={
              <Space>
                <Button 
                  icon={<RollbackOutlined />} 
                  onClick={() => setSelectedStudent(null)}
                >
                  返回学员列表
                </Button>
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />} 
                  onClick={() => loadAchievements(selectedStudent.id)}
                >
                  重试
                </Button>
              </Space>
            }
          />
        </div>
      );
    }
    
    // 添加样式，确保图片显示正确
    const imageStyle = {
      width: '220px',
      height: '160px',
      objectFit: 'cover' as const,
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    };
    
    return (
      <div className="achievements-container">
        <div className="section-header">
          <Space>
            <Button 
              icon={<RollbackOutlined />} 
              onClick={() => setSelectedStudent(null)}
            >
              返回学员列表
            </Button>
            <Title level={4}>
              {selectedStudent.name}的学习成果
            </Title>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => loadAchievements(selectedStudent.id)}
              size="small"
            >
              刷新
            </Button>
          </Space>
        </div>
        
        {achievements.length === 0 ? (
          <Empty 
            description={
              <div>
                <p>该学员暂无学习成果记录</p>
                <p className="empty-note">学习成果由家长添加，教师可以查看</p>
              </div>
            } 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Space direction="vertical">
              {/* 可以添加一个提示按钮 */}
              <Button 
                type="link" 
                onClick={() => message.info('请通知家长添加学生的学习成果')}
              >
                如何添加学习成果?
              </Button>
              {/* 添加刷新按钮让用户可以重试 */}
              <Button 
                type="primary" 
                onClick={() => loadAchievements(selectedStudent.id)}
                icon={<ReloadOutlined />}
              >
                刷新
              </Button>
            </Space>
          </Empty>
        ) : (
          <List
            itemLayout="vertical"
            dataSource={achievements}
            renderItem={(item: StudentAchievement, index: number) => {
              // 验证学习成果项数据完整性
              if (!item || !item.id) {
                console.warn(`学习成果项格式无效:`, item);
                return null;
              }
              
              // 规范化日期显示
              const displayDate = item.achievementDate 
                ? moment(item.achievementDate).format('YYYY-MM-DD')
                : '未知日期';
                
              // 确保图片URL有效
              const hasValidImage = !!item.imageUrl && typeof item.imageUrl === 'string' && item.imageUrl.trim() !== '';
              
              return (
                <List.Item
                  key={item.id}
                  className="achievement-item"
                  style={{ borderBottom: '1px solid #f0f0f0', padding: '16px 0' }}
                >
                  <div className="achievement-content" style={{ display: 'flex' }}>
                    {/* 序号 */}
                    <div className="achievement-index" style={{ 
                      flexShrink: 0, 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      background: '#1890ff', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      marginRight: '16px',
                      marginTop: '8px'
                    }}>
                      {index + 1}
                    </div>
                    
                    {/* 内容区域 */}
                    <div className="achievement-details" style={{ flex: 1 }}>
                      {/* 日期和标题 */}
                      <div className="achievement-header" style={{ marginBottom: '12px' }}>
                        <Tag color="blue">
                          {displayDate}
                        </Tag>
                        <span className="achievement-title" style={{ 
                          marginLeft: '8px', 
                          fontSize: '16px', 
                          fontWeight: 500 
                        }}>
                          {item.title || '未命名成果'}
                        </span>
                      </div>
                      
                      {/* 描述 */}
                      <Paragraph 
                        className="achievement-description"
                        style={{ margin: '12px 0' }}
                      >
                        {item.description || '无描述'}
                      </Paragraph>
                      
                      {/* 证明图片 */}
                      {hasValidImage ? (
                        <div className="achievement-image">
                          <div className="achievement-image-container">
                            <Image
                              width={220}
                              height={160}
                              src={item.imageUrl}
                              alt={`${item.title || '学习成果'}证明图片`}
                              fallback={`${process.env.PUBLIC_URL}/images/default-image.jpg`}
                              style={imageStyle}
                              preview={{
                                src: item.imageUrl,
                                mask: (
                                  <div>
                                    <span>查看大图</span>
                                  </div>
                                )
                              }}
                              onError={(e) => {
                                console.error('图片加载失败:', item.imageUrl);
                                const imgElement = e.currentTarget as HTMLImageElement;
                                imgElement.src = `${process.env.PUBLIC_URL}/images/default-image.jpg`;
                              }}
                            />
                            <div className="achievement-image-badge">证明图片</div>
                          </div>
                        </div>
                      ) : (
                        <div className="achievement-no-image">
                          <div className="no-image-text">未上传证明图片</div>
                        </div>
                      )}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    );
  };
  
  // 设置快速时间段
  const setQuickTimeSlot = (index: number, startHour: number, endHour: number, minutes: number = 0, endMinutes: number = 0) => {
    console.log('设置快速时间段:', index, startHour, endHour, minutes, endMinutes);
    
    // 使用当前日期作为基准
    const today = new Date();
    
    // 创建新的开始时间
    const newStartTime = new Date(today);
    newStartTime.setHours(startHour, minutes, 0, 0);
    
    // 创建新的结束时间
    const newEndTime = new Date(today);
    
    // 处理跨日情况：如果结束小时小于开始小时，则设置为第二天
    if (endHour < startHour || (endHour === startHour && endMinutes <= minutes)) {
      newEndTime.setDate(today.getDate() + 1);
    }
    
    newEndTime.setHours(endHour, endMinutes, 0, 0);
    
    console.log('生成的时间:', {
      startTime: moment(newStartTime).format('YYYY-MM-DD HH:mm:ss'),
      endTime: moment(newEndTime).format('YYYY-MM-DD HH:mm:ss')
    });
    
    // 验证时间有效性
    if (newStartTime >= newEndTime) {
      message.error('排课时段无效：结束时间必须晚于开始时间');
      return;
    }
    
    // 更新时间槽 - 创建全新对象避免引用问题
    const updatedTimeSlots = [...timeSlots];
    updatedTimeSlots[index] = {
      ...updatedTimeSlots[index],
      startTime: new Date(newStartTime.getTime()),
      endTime: new Date(newEndTime.getTime())
    };
    
    setTimeSlots(updatedTimeSlots);
    console.log('快速时间槽已更新:', updatedTimeSlots[index]);
  };
  
  // 设置常用的时间段
  const renderQuickTimeButtons = (slotIndex: number) => {
    // 常用时间段定义
    const commonTimeSlots = [
      // 1.5小时类型
      { label: '上午 10:00-11:30', startHour: 10, endHour: 11, minutes: 0, endMinutes: 30, type: '1.5h' },
      { label: '下午 13:30-15:00', startHour: 13, endHour: 15, minutes: 30, endMinutes: 0, type: '1.5h' },
      { label: '下午 15:30-17:00', startHour: 15, endHour: 17, minutes: 30, endMinutes: 0, type: '1.5h' },
      { label: '晚上 18:30-20:00', startHour: 18, endHour: 20, minutes: 30, endMinutes: 0, type: '1.5h' },
      { label: '晚上 20:30-22:00', startHour: 20, endHour: 22, minutes: 30, endMinutes: 0, type: '1.5h' },
      // 2小时类型
      { label: '上午 10:00-12:00', startHour: 10, endHour: 12, minutes: 0, endMinutes: 0, type: '2h' },
      { label: '下午 13:30-15:30', startHour: 13, endHour: 15, minutes: 30, endMinutes: 30, type: '2h' },
      { label: '下午 16:00-18:00', startHour: 16, endHour: 18, minutes: 0, endMinutes: 0, type: '2h' },
      { label: '晚上 19:00-21:00', startHour: 19, endHour: 21, minutes: 0, endMinutes: 0, type: '2h' },
    ];
    
    // 将时间段按类型分组
    const timeSlotsByType = {
      '1.5h': commonTimeSlots.filter(slot => slot.type === '1.5h'),
      '2h': commonTimeSlots.filter(slot => slot.type === '2h')
    };
    
    return (
      <>
        {/* 1.5小时时间段 */}
        <Col span={24} style={{ marginTop: '8px', marginBottom: '4px' }}>
          <Text strong>1.5小时课程:</Text>
        </Col>
        {timeSlotsByType['1.5h'].map((slot, idx) => (
          <Col key={`1.5h-${idx}`} span={8}>
            <Button 
              size="small" 
              style={{ width: '100%' }}
              onClick={() => setQuickTimeSlot(slotIndex, slot.startHour, slot.endHour, slot.minutes, slot.endMinutes)}
            >
              {slot.label}
            </Button>
          </Col>
        ))}
        
        {/* 2小时时间段 */}
        <Col span={24} style={{ marginTop: '12px', marginBottom: '4px' }}>
          <Text strong>2小时课程:</Text>
        </Col>
        {timeSlotsByType['2h'].map((slot, idx) => (
          <Col key={`2h-${idx}`} span={8}>
            <Button 
              size="small" 
              style={{ width: '100%' }}
              onClick={() => setQuickTimeSlot(slotIndex, slot.startHour, slot.endHour, slot.minutes, slot.endMinutes)}
            >
              {slot.label}
            </Button>
          </Col>
        ))}
      </>
    );
  };
  
  // 修改 addTimeSlot 函数，创建空的时间段
  const addTimeSlot = (startTime?: Date, endTime?: Date) => {
    console.log('添加时间槽:', startTime, endTime);
    
    // 创建一个新对象，避免引用问题
    let newSlot;
    
    // 如果提供了具体的时间参数，则使用这些参数
    if (startTime || endTime) {
      newSlot = { 
        startTime: startTime ? new Date(startTime.getTime()) : new Date(), 
        endTime: endTime ? new Date(endTime.getTime()) : new Date() 
      };
    } else {
      // 创建空的时间段，不设置默认值
      newSlot = { 
        startTime: null as unknown as Date, 
        endTime: null as unknown as Date 
      };
    }
    
    // 更新状态
    setTimeSlots([...timeSlots, newSlot]);
    console.log('时间槽已添加，总数:', timeSlots.length + 1);
  };
  
  // 移除排课时段
  const removeTimeSlot = (index: number) => {
    const updatedTimeSlots = [...timeSlots];
    updatedTimeSlots.splice(index, 1);
    setTimeSlots(updatedTimeSlots);
  };
  
  // 更新时段开始时间
  const updateStartTime = (index: number, date: Date | null) => {
    console.log('更新开始时间:', index, date ? date.toISOString() : null);
    // 创建一个全新的数组，避免引用问题
    const updatedTimeSlots = [...timeSlots];
    
    // 如果date存在，创建一个新的Date对象，否则设为null
    updatedTimeSlots[index] = {
      ...updatedTimeSlots[index],
      startTime: date ? new Date(date.getTime()) : null as unknown as Date
    };
    
    // 检查开始时间是否晚于结束时间（如果结束时间已设置）
    if (date && updatedTimeSlots[index].endTime && date >= updatedTimeSlots[index].endTime) {
      message.warning('开始时间必须早于结束时间，请重新设置');
      // 不更新状态，直接返回
      return;
    }
    
    // 更新状态
    setTimeSlots(updatedTimeSlots);
    console.log('时间槽已更新:', updatedTimeSlots.map(slot => ({
      startTime: slot.startTime ? slot.startTime.toISOString() : null,
      endTime: slot.endTime ? slot.endTime.toISOString() : null
    })));
  };
  
  // 更新时段结束时间
  const updateEndTime = (index: number, date: Date | null) => {
    console.log('更新结束时间:', index, date ? date.toISOString() : null);
    // 创建一个全新的数组，避免引用问题
    const updatedTimeSlots = [...timeSlots];
    
    // 如果date存在，创建一个新的Date对象，否则设为null
    updatedTimeSlots[index] = {
      ...updatedTimeSlots[index],
      endTime: date ? new Date(date.getTime()) : null as unknown as Date
    };
    
    // 检查结束时间是否早于开始时间（如果开始时间已设置）
    if (date && updatedTimeSlots[index].startTime && date <= updatedTimeSlots[index].startTime) {
      message.warning('结束时间必须晚于开始时间，请重新设置');
      // 不更新状态，直接返回
      return;
    }
    
    // 更新状态
    setTimeSlots(updatedTimeSlots);
    console.log('时间槽已更新:', updatedTimeSlots.map(slot => ({
      startTime: slot.startTime ? slot.startTime.toISOString() : null,
      endTime: slot.endTime ? slot.endTime.toISOString() : null
    })));
  };
  
  // 提交排课信息
  const handleScheduleSubmit = async () => {
    // 检查所有时段是否都已设置时间
    const hasEmptyTimeSlots = timeSlots.some(
      slot => !slot.startTime || !slot.endTime
    );
    
    if (hasEmptyTimeSlots) {
      message.error('请设置所有时段的开始和结束时间');
      return;
    }
    
    // 检查时段是否有效
    const isValid = timeSlots.every(slot => slot.startTime < slot.endTime);
    
    if (!isValid) {
      message.error('排课时段无效，结束时间必须晚于开始时间');
      return;
    }
    
    if (!selectedStudentForSchedule) {
      message.error('请选择学生');
      return;
    }
    
    try {
      // 显示加载中提示
      const loadingMsg = message.loading('正在检查时间冲突并提交排课信息...', 0);
      
      // 检查并记录要发送的时间段
      console.log('提交的时间段详情:');
      timeSlots.forEach((slot, slotIndex) => {
        console.log(`时段 ${slotIndex + 1}:`, {
          startTime: slot.startTime ? moment(slot.startTime).format('YYYY-MM-DD HH:mm:ss') : '未设置',
          endTime: slot.endTime ? moment(slot.endTime).format('YYYY-MM-DD HH:mm:ss') : '未设置',
          startTimeObject: slot.startTime ? slot.startTime.toISOString() : null,
          endTimeObject: slot.endTime ? slot.endTime.toISOString() : null
        });
      });
      
      // 为每个时段创建课程安排
      const results = [];
      let hasCreatedAtLeastOne = false;
      let conflictCount = 0;
      let errorCount = 0;
      let authErrorCount = 0;
      
      // 增加重试机制的变量
      let maxRetries = 3;
      let retryDelay = 1000; // 毫秒
      
      // 使用for...of循环，但添加索引计数器
      let slotIndex = 0;
      for (const slot of timeSlots) {
        try {
          console.log(`处理时间段 ${slotIndex + 1}: ${moment(slot.startTime).format('YYYY-MM-DD HH:mm')} - ${moment(slot.endTime).format('HH:mm')}`);
          
          // 先检查时间冲突
          let hasConflict = false;
          try {
            // 确保startTime和endTime是有效的Date对象
            if (!slot.startTime || !slot.endTime) {
              message.error(`时段 ${slotIndex + 1} 的时间设置不完整`);
              slotIndex++; // 更新索引
              continue;
            }
            
            // 重新创建日期对象以确保数据的一致性和干净
            const safeStartTime = new Date(slot.startTime.getTime());
            const safeEndTime = new Date(slot.endTime.getTime());
            
            // 转换为ISO字符串前再次检查时间对象有效性
            const startTimeISO = safeStartTime.toISOString();
            const endTimeISO = safeEndTime.toISOString();
            
            console.log(`检查时段 ${slotIndex + 1} 冲突:`, {
              startTime: startTimeISO,
              endTime: endTimeISO
            });
            
            // 检查两个时间是否符合逻辑顺序（开始时间早于结束时间）
            if (safeStartTime >= safeEndTime) {
              message.error(`时段 ${slotIndex + 1} 时间无效：结束时间必须晚于开始时间`);
              slotIndex++; // 更新索引
              continue;
            }
            
            const conflictCheck = await scheduleAPI.checkTimeConflict(
              user?.userId as number,
              selectedStudentForSchedule.id,
              startTimeISO,
              endTimeISO
            );
            
            // 如果有冲突
            hasConflict = conflictCheck.hasConflict;
          } catch (conflictError) {
            // 冲突检查失败但不影响创建过程
            console.warn('时间冲突检查失败，但将继续尝试创建课程:', conflictError);
            // 默认视为没有冲突
            hasConflict = false;
          }
          
          if (hasConflict) {
            console.warn(`时段冲突: ${moment(slot.startTime).format('YYYY-MM-DD HH:mm')} - ${moment(slot.endTime).format('HH:mm')}`);
            message.warning(`时段 ${moment(slot.startTime).format('YYYY-MM-DD HH:mm')} 到 ${moment(slot.endTime).format('HH:mm')} 有时间冲突，已跳过`);
            conflictCount++;
            slotIndex++; // 更新索引
            continue; // 跳过这个时段，处理下一个
          }
          
          // 再次确认时间有效性并重新创建ISO字符串
          if (!slot.startTime || !slot.endTime) {
            message.error(`时段 ${slotIndex + 1} 的时间设置不完整`);
            slotIndex++; // 更新索引
            continue;
          }
          
          // 确保创建全新的Date对象并正确格式化为ISO字符串
          const finalStartTime = new Date(slot.startTime.getTime());
          const finalEndTime = new Date(slot.endTime.getTime());
          
          // 确保ISO字符串格式正确 - 使用标准的toISOString()方法，不进行自定义格式化
          const startTimeISO = finalStartTime.toISOString();
          const endTimeISO = finalEndTime.toISOString();
          
          console.log('最终时间格式:', {
            finalStartTime: finalStartTime.toString(),
            finalEndTime: finalEndTime.toString(),
            startTimeISO,
            endTimeISO
          });
          
          // 准备课程数据
          const scheduleData = {
            teacherId: user?.userId,
            studentId: selectedStudentForSchedule.id,
            startTime: startTimeISO, 
            endTime: endTimeISO,
            courseContent: selectedStudentForSchedule.courseType || `${selectedStudentForSchedule.name}的课程`,
            status: 'SCHEDULED'
          };
          
          console.log('准备创建课程的完整数据:', JSON.stringify(scheduleData));
          
          // 实现重试逻辑
          let retryCount = 0;
          let success = false;
          let lastError;
          
          while (retryCount < maxRetries && !success) {
            try {
              // 若非首次尝试，添加重试信息
              if (retryCount > 0) {
                console.log(`第${retryCount}次重试创建课程`);
                // 延迟一段时间再重试
                await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount));
              }
              
              // 调用API创建课程
              const result = await scheduleAPI.createSchedule(scheduleData);
              results.push(result);
              hasCreatedAtLeastOne = true;
              success = true;
              
              console.log('课程创建成功:', result);
              
              // 创建新的课程事件
              const newEvent = {
                id: result.id,
                title: `课程 - ${selectedStudentForSchedule.name}`,
                start: finalStartTime,
                end: finalEndTime,
                studentId: selectedStudentForSchedule.id,
                studentName: selectedStudentForSchedule.name,
                courseType: selectedStudentForSchedule.courseType
              };
              
              console.log('向日历添加新事件:', {
                id: result.id,
                startTime: moment(finalStartTime).format('YYYY-MM-DD HH:mm:ss'),
                endTime: moment(finalEndTime).format('YYYY-MM-DD HH:mm:ss')
              });
              
              // 更新课程事件
              setCourseEvents(prev => [...prev, newEvent]);
              
            } catch (retryError) {
              lastError = retryError;
              retryCount++;
              
              console.error('创建课程失败:', retryError);
              
              // 检查是否为403错误
              if (axios.isAxiosError(retryError) && retryError.response?.status === 403) {
                console.warn(`权限错误(403)，第${retryCount}次重试`);
                
                // 记录403错误
                if (retryCount === maxRetries) {
                  authErrorCount++;
                }
              } else if (axios.isAxiosError(retryError) && retryError.response?.status === 500) {
                // 服务器内部错误，记录详细信息以便调试
                console.error('服务器内部错误:', retryError.response?.data);
                
                // 非403错误不再重试
                break;
              } else {
                // 其他错误不再重试
                break;
              }
            }
          }
          
          // 如果所有重试都失败
          if (!success) {
            throw lastError;
          }
          
        } catch (error: unknown) {
          errorCount++;
          console.error('创建课程安排失败:', error);
          
          // 尝试提取更有用的错误信息
          let errorMessage = '创建失败';
          
          if (axios.isAxiosError(error) && error.response) {
            // 有响应但状态码异常
            if (error.response.status === 400 && error.response.data?.message?.includes('时间冲突')) {
              errorMessage = `时间冲突`;
              conflictCount++;
            } else if (error.response.status === 403) {
              errorMessage = `权限不足 (403)`;
              authErrorCount++;
            } else if (error.response.status === 500) {
              errorMessage = `服务器内部错误 (500)`;
              // 对于500错误，显示更详细的错误信息
              if (error.response.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
              }
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            } else {
              errorMessage = `错误 (${error.response.status})`;
            }
          } else if (error instanceof Error) {
            // 网络错误或其他JS错误
            errorMessage = error.message;
          }
          
          message.error(`时段 ${moment(slot.startTime).format('MM-DD HH:mm')} 创建失败: ${errorMessage}`);
        }
        
        // 在每次循环结束时递增索引
        slotIndex++;
      }
      
      // 关闭加载提示
      loadingMsg();
      
      // 处理各种情况
      if (hasCreatedAtLeastOne) {
        // 至少成功创建了一个
        message.success(`成功创建 ${results.length} 个课程安排！`);
        
        // 提示冲突和错误
        if (conflictCount > 0) {
          message.warning(`${conflictCount}个时段因冲突被跳过`);
        }
        if (errorCount > 0) {
          message.warning(`${errorCount}个时段因错误创建失败`);
        }
        if (authErrorCount > 0) {
          Modal.warning({
            title: '权限提示',
            content: `有${authErrorCount}个时段因权限问题创建失败。这可能是因为系统权限设置问题，已经尝试了所有可能的解决方案。请联系系统管理员处理权限问题。`,
            okText: '知道了'
          });
        }
        
        // 关闭表单和模态框
        setTimeSlots([]);
        setScheduleFormVisible(false);
        setSelectedStudentForSchedule(null);
        setScheduleModalVisible(false);
      } else if (conflictCount > 0 && errorCount === 0) {
        // 全部是冲突问题
        message.warning(`所有时段都存在冲突，未能创建任何课程`);
      } else if (authErrorCount > 0 && errorCount > 0) {
        // 全部是权限错误
        Modal.error({
          title: '权限错误',
          content: '无法创建课程，系统返回权限错误(403)。请联系系统管理员检查您的账户权限设置。',
          okText: '知道了'
        });
      } else if (errorCount > 0) {
        // 存在错误
        message.error(`创建失败：${errorCount}个时段因错误无法创建`);
      } else {
        // 未知情况
        message.warning('未能成功创建任何课程安排');
      }
    } catch (error: unknown) {
      console.error('创建课程安排失败:', error);
      let errorMessage = '未知错误';
      
      if (axios.isAxiosError(error)) {
        // 处理Axios错误
        if (error.response?.status === 403) {
          errorMessage = '权限错误(403)：您没有权限创建课程';
          Modal.error({
            title: '权限错误',
            content: '系统返回权限错误(403)。请联系系统管理员检查您的账户权限设置。',
            okText: '知道了'
          });
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `请求错误: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      message.error('创建课程安排失败: ' + errorMessage);
    }
  };
  
  // 开始为特定学生排课
  const startScheduleForStudent = (student: StudentResponse) => {
    setSelectedStudentForSchedule(student);
    setScheduleFormVisible(true);
    setTimeSlots([]);
  };
  
  // 渲染课程安排日历组件
  const renderCourseSchedule = () => {
    // 创建每天8:00 AM和9:00 PM的时间对象
    const minTime = new Date();
    minTime.setHours(8, 0, 0);
    
    const maxTime = new Date();
    maxTime.setHours(21, 0, 0);
    
    return (
      <div className="course-schedule-container">
        <div className="section-header">
          <Title level={4}>课程安排</Title>
          <Space style={{ marginRight: '80px' }}>
            <Button 
              type="primary" 
              onClick={() => setScheduleModalVisible(true)}
            >
              开始排课
            </Button>
          </Space>
        </div>
        
        <div className="calendar-container" style={{ height: 'calc(100vh - 180px)' }}>
          <Calendar
            localizer={localizer}
            events={courseEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['month', 'week']}
            defaultView='week'
            min={minTime}
            max={maxTime}
            formats={{
              dayHeaderFormat: (date) => moment(date).format('YYYY年MM月DD日'),
              dayRangeHeaderFormat: ({ start, end }) => 
                `${moment(start).format('YYYY年MM月DD日')} - ${moment(end).format('MM月DD日')}`,
            }}
            messages={{
              today: '今天',
              previous: '上一个',
              next: '下一个',
              month: '月视图',
              week: '周视图',
              date: '日期',
              time: '时间',
              event: '事件',
              showMore: (total) => `+${total}更多`
            }}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventPropGetter}
            components={{
              month: {
                event: monthEvent
              },
              week: {
                event: weekEvent
              },
              toolbar: CustomToolbar
            }}
          />
        </div>
        
        {/* 排课模态框 */}
        <Modal
          title="选择学员排课"
          open={scheduleModalVisible}
          onCancel={() => {
            setScheduleModalVisible(false);
            setSelectedStudentForSchedule(null);
            setScheduleFormVisible(false);
            setTimeSlots([]);
          }}
          footer={null}
          width={800}
        >
          {scheduleFormVisible ? (
            <div className="schedule-form">
              <div style={{ marginBottom: '16px' }}>
                <Space align="center">
                  <Button onClick={() => {
                    setScheduleFormVisible(false);
                    setSelectedStudentForSchedule(null);
                  }} icon={<RollbackOutlined />}>
                    返回学员列表
                  </Button>
                  <Title level={5} style={{ margin: 0 }}>
                    为 {selectedStudentForSchedule?.name} 排课
                  </Title>
                </Space>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <Row gutter={16}>
                  <Col span={24}>
                    <Button type="dashed" onClick={() => addTimeSlot()} icon={<PlusOutlined />} block>
                      添加排课时段
                    </Button>
                  </Col>
                </Row>
              </div>
              
              {timeSlots.map((slot, index) => (
                <div key={index} style={{ marginBottom: '16px', padding: '16px', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
                  <Space align="baseline" style={{ marginBottom: '8px' }}>
                    <Title level={5} style={{ margin: 0 }}>排课时段 {index + 1}</Title>
                    <Button danger size="small" onClick={() => removeTimeSlot(index)}>
                      删除
                    </Button>
                  </Space>
                  
                  <Form layout="vertical">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="开始时间" required>
                          <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="选择开始时间"
                            value={slot.startTime ? moment(slot.startTime) : null}
                            onChange={(date) => updateStartTime(index, date ? date.toDate() : null)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="结束时间" required>
                          <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="选择结束时间"
                            value={slot.endTime ? moment(slot.endTime) : null}
                            onChange={(date) => updateEndTime(index, date ? date.toDate() : null)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* 添加快速选择时间段按钮 */}
                    <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
                      <Col span={24}>
                        <Text type="secondary">快速选择时间段:</Text>
                      </Col>
                      {renderQuickTimeButtons(index)}
                    </Row>
                    
                    {/* 添加连续排课选项 */}
                    {slot.startTime && slot.endTime && (
                      <Row gutter={[8, 8]} style={{ marginTop: '12px' }}>
                        <Col span={24}>
                          <Text type="secondary">连续排课:</Text>
                        </Col>
                        <Col span={12}>
                          <Button 
                            size="small" 
                            style={{ width: '100%' }}
                            onClick={() => handleMultiWeekSchedule(index, 4)}
                          >
                            连续排4周课
                          </Button>
                        </Col>
                        <Col span={12}>
                          <Button 
                            size="small" 
                            style={{ width: '100%' }}
                            onClick={() => handleMultiWeekSchedule(index, 8)}
                          >
                            连续排8周课
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </Form>
                </div>
              ))}
              
              <div style={{ marginTop: '24px', textAlign: 'right' }}>
                <Space>
                  <Button type="primary" onClick={handleScheduleSubmit}>
                    确认排课
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            <Table
              dataSource={students}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              columns={[
                {
                  title: '序号',
                  dataIndex: 'index',
                  key: 'index',
                  width: 80,
                  align: 'center' as const,
                  render: (_: any, __: any, index: number) => index + 1,
                },
                {
                  title: '姓名',
                  dataIndex: 'name',
                  key: 'name',
                  align: 'center' as const,
                },
                {
                  title: '操作',
                  key: 'action',
                  align: 'center' as const,
                  render: (_: any, record: StudentResponse) => (
                    <Button
                      type="primary"
                      onClick={() => startScheduleForStudent(record)}
                    >
                      排课
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </Modal>
        
        {/* 课程详情模态框 */}
        <Modal
          title={editingCourse ? "编辑课程" : "课程详情"}
          open={courseDetailModalVisible}
          onCancel={() => setCourseDetailModalVisible(false)}
          footer={null}
        >
          {selectedCourseEvent && (
            <div>
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="学生姓名">
                      <Input value={selectedCourseEvent.studentName} disabled />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="课程类型">
                      <Input value={selectedCourseEvent.courseType} disabled />
                    </Form.Item>
                  </Col>
                </Row>
                
                {editingCourse ? (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="开始时间">
                          <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="选择开始时间"
                            value={editedCourseStart ? moment(editedCourseStart) : null}
                            onChange={(date) => setEditedCourseStart(date ? date.toDate() : null as unknown as Date)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="结束时间">
                          <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="选择结束时间"
                            value={editedCourseEnd ? moment(editedCourseEnd) : null}
                            onChange={(date) => setEditedCourseEnd(date ? date.toDate() : null as unknown as Date)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="开始时间">
                          <Input 
                            value={moment(selectedCourseEvent.start).format('YYYY-MM-DD HH:mm')} 
                            disabled 
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="结束时间">
                          <Input 
                            value={moment(selectedCourseEvent.end).format('YYYY-MM-DD HH:mm')} 
                            disabled 
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="签到状态">
                          {selectedCourseEvent.checkedIn ? (
                            <Tag color="green">
                              学生已签到
                            </Tag>
                          ) : (
                            <Tag color="orange">未签到</Tag>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                
                <div style={{ textAlign: 'right', marginTop: '24px' }}>
                  <Space>
                    {editingCourse ? (
                      <>
                        <Button onClick={() => setEditingCourse(false)}>
                          取消编辑
                        </Button>
                        <Button 
                          type="primary" 
                          onClick={handleUpdateCourse}
                          loading={loading.courses}
                        >
                          保存修改
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => setCourseDetailModalVisible(false)}>
                          关闭
                        </Button>
                        <Button 
                          onClick={() => {
                            // 点击调整课程按钮时，清空时间值而不是使用原始值
                            setEditingCourse(true);
                            setEditedCourseStart(null as unknown as Date);
                            setEditedCourseEnd(null as unknown as Date);
                          }}
                        >
                          调整课程
                        </Button>
                        <Popconfirm
                          title="确定要取消这个课程吗?"
                          onConfirm={handleCancelCourse}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button 
                            danger
                            loading={loading.courses}
                          >
                            取消课程
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </Space>
                </div>
              </Form>
            </div>
          )}
        </Modal>
      </div>
    );
  };
  
  // 加载教师课程安排
  const loadTeacherCourses = useCallback(async () => {
    if (!user?.userId) {
      return;
    }
    
    setLoading(prev => ({ ...prev, courses: true }));
    
    try {
      const coursesData = await scheduleAPI.getTeacherSchedules(user.userId);
      
      if (Array.isArray(coursesData)) {
        // 将API返回的数据转换为Calendar组件所需的格式
        const events: CourseEvent[] = coursesData.map(course => {
          // 查找对应学生的授课类型
          const student = students.find(s => s.id === course.studentId);
          const studentCourseType = student ? student.courseType : undefined;
          
          return {
            id: course.id,
            title: `课程 - ${course.studentName}`,
            start: new Date(course.startTime),
            end: new Date(course.endTime),
            studentId: course.studentId,
            studentName: course.studentName,
            // 优先使用学生的授课类型，如果没有则使用课程内容
            courseType: studentCourseType || course.courseContent,
            checkedIn: course.checkedIn,
            checkInTime: course.checkInTime ? new Date(course.checkInTime) : undefined
          };
        });
        
        setCourseEvents(events);
        message.success(`成功加载${events.length}个课程安排`);
      }
    } catch (error) {
      console.error('加载课程安排失败:', error);
      message.error('加载课程安排失败，请稍后重试');
    } finally {
      setLoading(prev => ({ ...prev, courses: false }));
    }
  }, [user?.userId, students]);
  
  // 加载签到统计
  const loadCheckInStats = useCallback(async () => {
    if (!user?.userId) return;
    
    setLoadingStats(true);
    
    try {
      const startDate = statsDateRange[0].format('YYYY-MM-DDTHH:mm:ss');
      // 修改结束日期时间为当天的23:59:59，以确保包含最后一天的所有数据
      const endDate = statsDateRange[1].clone().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
      
      const stats = await scheduleAPI.getCheckInStatistics(
        user.userId, 
        startDate,
        endDate
      );
      
      setCheckInStats(stats);
    } catch (error) {
      console.error('获取签到统计失败:', error);
      message.error('获取签到统计失败，请稍后重试');
    } finally {
      setLoadingStats(false);
    }
  }, [user?.userId, statsDateRange]);
  
  // 将按照学生的签到率排序（从高到低）
  const sortedStudentStats = checkInStats?.studentStats 
    ? [...checkInStats.studentStats].sort((a, b) => b.checkInRate - a.checkInRate)
    : [];
  
  // 渲染签到统计组件
  const renderCheckInStatistics = () => {
    return (
      <div className="statistics-container">
        <div className="section-header">
          <Title level={4}>学生签到统计</Title>
          <Space>
            <DatePicker.RangePicker
              value={statsDateRange as any}
              onChange={handleDateRangeChange}
            />
            <Button 
              type="primary" 
              onClick={loadCheckInStats} 
              loading={loadingStats}
            >
              查询
            </Button>
          </Space>
        </div>
        
        {loadingStats ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : checkInStats ? (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="总课程数"
                      value={checkInStats.totalSchedules}
                      suffix="节"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="已签到课程"
                      value={checkInStats.checkedInCount}
                      suffix="节"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="出勤率"
                      value={checkInStats.checkInRate.toFixed(2)}
                      suffix="%"
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={24}>
              <Card title="学生签到率排名">
                <Table
                  dataSource={sortedStudentStats}
                  rowKey="studentId"
                  pagination={false}
                  columns={[
                    {
                      title: '排名',
                      key: 'ranking',
                      width: 80,
                      render: (_, __, index) => index + 1,
                    },
                    {
                      title: '学生姓名',
                      dataIndex: 'studentName',
                      key: 'studentName',
                    },
                    {
                      title: '总课程数',
                      dataIndex: 'totalSchedules',
                      key: 'totalSchedules',
                      sorter: (a, b) => a.totalSchedules - b.totalSchedules,
                    },
                    {
                      title: '已签到课程',
                      dataIndex: 'checkedInCount',
                      key: 'checkedInCount',
                      sorter: (a, b) => a.checkedInCount - b.checkedInCount,
                    },
                    {
                      title: '出勤率',
                      key: 'checkInRate',
                      render: (record) => `${record.checkInRate.toFixed(2)}%`,
                      sorter: (a, b) => a.checkInRate - b.checkInRate,
                      defaultSortOrder: 'descend',
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        ) : (
          <Empty description="暂无签到统计数据，请选择日期范围进行查询" />
        )}
      </div>
    );
  };
  
  // 渲染学生管理内容
  const renderStudentManagement = () => {
    // 如果已选择学生，显示学生学习成果
    if (selectedStudent) {
      return renderAchievementsList();
    } else {
      // 否则显示学生列表
      return renderStudentsList();
    }
  };
  
  // 渲染课程直播内容
  const renderCourseLive = () => {
    return (
      <div className="course-live-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '40px'
      }}>
        <Title level={3}>课程直播</Title>
        <Paragraph style={{ textAlign: 'center', marginBottom: '30px' }}>
          点击下方按钮进入课程直播平台
        </Paragraph>
        <Button
          type="primary"
          size="large"
          icon={<ReadOutlined />}
          onClick={() => window.open('https://dmtutors.live', '_blank')}
          style={{ fontSize: '16px', height: '50px', padding: '0 40px' }}
        >
          进入直播平台
        </Button>
      </div>
    );
  };

  // 渲染主内容
  const renderContent = () => {
    switch (activeMenuKey) {
      case '1': // 学生管理
        return renderStudentManagement();
      case '2': // 课程管理
        return renderCourseSchedule();
      case '3': // 签到统计
        return renderCheckInStatistics();
      case '4': // 课程直播
        return renderCourseLive();
      default:
        return renderStudentManagement();
    }
  };
  
  // 在切换到签到统计时加载数据
  useEffect(() => {
    if (user?.userId && activeMenuKey === '3') {
      loadCheckInStats();
    }
  }, [user?.userId, activeMenuKey, loadCheckInStats]);

  // 处理课程点击事件
  const handleSelectEvent = (event: CourseEvent) => {
    setSelectedCourseEvent(event);
    setEditedCourseStart(new Date(event.start));
    setEditedCourseEnd(new Date(event.end));
    setCourseDetailModalVisible(true);
    setEditingCourse(false);
  };
  
  // 自定义事件渲染方法
  const eventPropGetter = useCallback(
    (event: CourseEvent) => {
      return {
        style: {
          backgroundColor: '#1890ff',
        }
      };
    },
    []
  );

  // 自定义月视图事件渲染
  const monthEvent = useCallback(
    ({ event }: { event: CourseEvent }) => (
      <div>
        <span>{event.studentName}</span>
      </div>
    ),
    []
  );

  // 自定义周视图事件渲染
  const weekEvent = useCallback(
    ({ event }: { event: CourseEvent }) => (
      <div>
        <div><strong>{event.studentName}</strong></div>
        <div>{event.courseType}</div>
      </div>
    ),
    []
  );
  
  // 处理课程修改
  const handleUpdateCourse = async () => {
    if (!selectedCourseEvent) {
      message.error('课程信息不完整');
      return;
    }
    
    if (!editedCourseStart || !editedCourseEnd) {
      message.error('请选择课程开始和结束时间');
      return;
    }
    
    if (editedCourseStart >= editedCourseEnd) {
      message.error('结束时间必须晚于开始时间');
      return;
    }
    
    try {
      setLoading(prev => ({...prev, courses: true}));
      
      // 确保日期对象格式正确
      const finalStartTime = new Date(editedCourseStart.getTime());
      const finalEndTime = new Date(editedCourseEnd.getTime());
      
      // 转换为ISO格式字符串
      const startTimeISO = finalStartTime.toISOString();
      const endTimeISO = finalEndTime.toISOString();
      
      console.log('更新课程时间:', {
        原始开始时间: editedCourseStart,
        原始结束时间: editedCourseEnd,
        最终开始时间: finalStartTime,
        最终结束时间: finalEndTime,
        开始时间ISO: startTimeISO,
        结束时间ISO: endTimeISO
      });
      
      // 准备更新数据
      const updateData = {
        id: selectedCourseEvent.id,
        teacherId: user?.userId,
        studentId: selectedCourseEvent.studentId,
        startTime: startTimeISO,
        endTime: endTimeISO,
        courseContent: selectedCourseEvent.courseType || `${selectedCourseEvent.studentName}的课程`,
        status: 'SCHEDULED'
      };
      
      console.log('准备提交的更新数据:', JSON.stringify(updateData));
      
      // 调用API更新课程
      const result = await scheduleAPI.updateSchedule(selectedCourseEvent.id, updateData);
      
      // 更新本地数据
      setCourseEvents(prev => 
        prev.map(event => 
          event.id === selectedCourseEvent.id 
            ? {
                ...event,
                start: finalStartTime,
                end: finalEndTime
              } 
            : event
        )
      );
      
      message.success('课程已成功更新');
      setCourseDetailModalVisible(false);
    } catch (error) {
      console.error('更新课程失败:', error);
      message.error('更新课程失败，请稍后重试');
    } finally {
      setLoading(prev => ({...prev, courses: false}));
    }
  };
  
  // 处理课程取消
  const handleCancelCourse = async () => {
    if (!selectedCourseEvent) {
      return;
    }
    
    try {
      setLoading(prev => ({...prev, courses: true}));
      
      // 调用API删除课程（而不是仅标记为已取消）
      await scheduleAPI.deleteSchedule(selectedCourseEvent.id);
      
      // 从本地数据中移除
      setCourseEvents(prev => prev.filter(event => event.id !== selectedCourseEvent.id));
      
      message.success('课程已成功删除');
      setCourseDetailModalVisible(false);
    } catch (error) {
      console.error('删除课程失败:', error);
      message.error('删除课程失败，请稍后重试');
    } finally {
      setLoading(prev => ({...prev, courses: false}));
    }
  };

  // 在DatePicker.RangePicker中使用类型断言解决类型不匹配问题
  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setStatsDateRange(dates as [moment.Moment, moment.Moment]);
    }
  };

  // 处理连续多周排课
  const handleMultiWeekSchedule = (index: number, weekCount: number) => {
    // 确保有有效的起始时间槽
    if (!timeSlots[index] || !timeSlots[index].startTime || !timeSlots[index].endTime) {
      message.error('请先设置有效的起始时间段');
      return;
    }
    
    const baseStartTime = new Date(timeSlots[index].startTime.getTime());
    const baseEndTime = new Date(timeSlots[index].endTime.getTime());
    
    // 获取当前时间槽的开始和结束时间之间的小时和分钟差
    const durationMs = baseEndTime.getTime() - baseStartTime.getTime();
    
    // 创建新的时间槽数组
    const newTimeSlots = [...timeSlots];
    
    // 添加后续周的时间槽
    for (let week = 1; week < weekCount; week++) {
      // 计算新的开始时间（当前时间 + 一周的毫秒数 * 周数）
      const newStartTime = new Date(baseStartTime.getTime() + (7 * 24 * 60 * 60 * 1000 * week));
      // 计算新的结束时间（新开始时间 + 原始持续时间）
      const newEndTime = new Date(newStartTime.getTime() + durationMs);
      
      // 添加新的时间槽
      newTimeSlots.push({
        startTime: newStartTime,
        endTime: newEndTime
      });
    }
    
    // 更新状态
    setTimeSlots(newTimeSlots);
    
    // 提示用户
    message.success(`已成功添加${weekCount-1}个后续周的相同时间段，共${weekCount}周课程`);
  };

  return (
    <Layout>
      <Sider
        width={200}
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
        }}
      >
        <div className="logo" style={{ height: '64px', background: '#001529', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DashboardOutlined style={{ fontSize: '20px', marginRight: collapsed ? '0' : '8px' }} />
          {!collapsed && <span>教师控制面板</span>}
        </div>
        {sideMenu}
      </Sider>
      <Layout className="site-layout" style={{ marginLeft: collapsed ? 80 : 200 }}>
        <Header style={{ padding: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ marginLeft: '16px' }}>
            <Button 
              type="text" 
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} 
              onClick={toggleSidebar}
            />
          </div>
          <div style={{ marginRight: '16px' }}>
            <Space>
              <span>欢迎，{user?.username || '教师'}</span>
              <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
            </Space>
          </div>
        </Header>
        <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default TeacherDashboard; 